<template>
  <v-app id="app_measurements">
    <v-container fluid>

      <!-- Loading Overlay -->
      <v-overlay v-model="tableLoading" class="align-center justify-center">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <!-- Header -->
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">BULK UPLOAD MANAGEMENT</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">
            Set up a new bulkupload with a code that fits your needs.
          </p>
          <div v-if="lastBulkUploadTime" class="mt-2">
            <v-chip
              color="#800080"
              variant="tonal"
              size="small"
              prepend-icon="mdi-clock-outline"
            >
              Last Bulk Upload: {{ formattedLastUploadTime }}
            </v-chip>
          </div>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-btn color="#800080" size="x-large" variant="text" @click="AreaBulkUploadDialog = true">
              <v-icon>mdi-file-upload-outline</v-icon>&nbsp;&nbsp; BULK UPLOAD
            </v-btn>
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <!-- Main Card -->
      <v-card class="custom_card_box mt-4" elevation="1" border="sm opacity-1" rounded="lg">

        <!-- Toolbar with Delete Button -->
        <v-card-text class="custom_tool_bar_style">
          <!-- <v-btn
            v-if="permissions.delete && isAdministrator && totalOutstandingsCount > 0"
            class="custom_delete_btn"
            variant="flat"
            @click="confirmDeleteAll"
            :loading="deleteAllLoading"
          >
            <v-icon size="large">mdi-delete</v-icon>&nbsp;&nbsp;DELETE ALL
          </v-btn> -->
        </v-card-text>

        <v-divider thickness="2"></v-divider>

        <!-- Filters -->
        <v-expansion-panels v-model="panelOpen" multiple>
          <v-expansion-panel value="filters" elevation="0">
            <v-expansion-panel-title expand-icon="mdi-menu-down">
              <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
              <span class="text-body-1 font-weight-bold">FILTERS</span>
              <v-chip
                v-if="hasActiveFilters"
                color="primary"
                size="x-small"
                class="ml-2"
              >
                Active
              </v-chip>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row class="mt-2" dense>

                <!-- Date From -->
                <v-col cols="12" md="3" class="py-1">
                  <v-text-field
                    v-model="filters.date_from"
                    label="Date From"
                    type="date"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    hide-details
                    :append-inner-icon="filters.date_from ? 'mdi-close-circle' : ''"
                    @click:append-inner="clearDateFilter('date_from')"
                  ></v-text-field>
                </v-col>

                <!-- Date To -->
                <v-col cols="12" md="3" class="py-1">
                  <v-text-field
                    v-model="filters.date_to"
                    label="Date To"
                    type="date"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    hide-details
                    :append-inner-icon="filters.date_to ? 'mdi-close-circle' : ''"
                    @click:append-inner="clearDateFilter('date_to')"
                  ></v-text-field>
                </v-col>

                <!-- Document Type (Hardcoded Dropdown) -->
                <v-col cols="12" md="3" class="py-1">
                  <v-select
                    v-model="filters.document_type"
                    :items="documentTypes"
                    label="Document Type"
                    placeholder="Select type"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    clearable
                    hide-details
                  ></v-select>
                </v-col>

                <!-- AR Company (Hardcoded Dropdown) -->
                <v-col cols="12" md="3" class="py-1">
                  <v-autocomplete
                    v-model="filters.ar_company"
                    :items="arCompanies"
                    label="AR Company"
                    placeholder="Select company"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    clearable
                    hide-details
                  ></v-autocomplete>
                </v-col>

                <!-- Area Code (Loaded from /api/area/user/{userId}) -->
                <v-col cols="12" md="3" class="py-1">
                  <v-autocomplete
                    v-model="filters.area_code"
                    :items="areaCodes"
                    :loading="areaCodesLoading"
                    label="Area Code"
                    placeholder="Select area"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    clearable
                    hide-details
                  ></v-autocomplete>
                </v-col>

                <!-- Payment Status Filter -->
                <v-col cols="12" md="3" class="py-1">
                  <v-select
                    v-model="filters.status"
                    :items="paymentStatusOptions"
                    label="Payment Status"
                    placeholder="Select payment status"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    clearable
                    hide-details
                    item-title="title"
                    item-value="value"
                  ></v-select>
                </v-col>

                <!-- Is Active Filter -->
                <v-col cols="12" md="3" class="py-1">
                  <v-select
                    v-model="filters.is_active"
                    :items="activeStatusOptions"
                    label="Document Active Status"
                    placeholder="Select active status"
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    hide-details
                    item-title="title"
                    item-value="value"
                    :clearable="filters.is_active !== null"
                  ></v-select>
                </v-col>

                <!-- Search -->
                <v-col cols="12" md="3" class="py-1">
                  <v-text-field
                    v-model="search"
                    clearable
                    label="Search"
                    placeholder="Search by Customer, Document No, etc."
                    variant="outlined"
                    density="comfortable"
                    class="custom_input_field_color"
                    hide-details
                  ></v-text-field>
                </v-col>

                <!-- Clear All Filters Button -->
                <v-col cols="12" class="py-1 text-right">
                  <v-btn
                    v-if="hasActiveFilters"
                    color="error"
                    variant="text"
                    size="small"
                    @click="clearAllFilters"
                    prepend-icon="mdi-filter-remove"
                  >
                    Clear All Filters
                  </v-btn>
                </v-col>

              </v-row>
              <v-divider class="mb-3 mt-2"></v-divider>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Table -->
        <v-card-text>
          <v-data-table
            :headers="headersOutstandubg"
            :items="outstandings"
            :items-per-page="itemsPerPage"
            :page="page"
            :server-items-length="totalOutstandingsCount"
            :loading="tableLoading"
            item-value="id"
            class="px-2 custom_table_curved"
            hide-default-footer
          >
            <template v-slot:loading>
              <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
            </template>

            <!-- Posting Date -->
            <template v-slot:[`item.posting_date`]="{ item }">
              <div class="column-content">
                {{ formatDate(item.posting_date) }}
              </div>
            </template>

            <!-- Customer No -->
            <template v-slot:[`item.customer_no`]="{ item }">
              <p class="text-body-2 mt-2 font-weight-medium">
                {{ item.customer_no || 'N/A' }}
              </p>
            </template>

            <!-- Area Code -->
            <template v-slot:[`item.area_code`]="{ item }">
              <p class="text-body-2 mt-2">
                {{ item.area_code || 'N/A' }}
              </p>
            </template>

            <!-- Name -->
            <template v-slot:[`item.name`]="{ item }">
              <p class="text-body-2 mt-2 font-weight-medium">
                {{ item.name || 'N/A' }}
              </p>
            </template>

            <!-- Discount Posting Group -->
            <template v-slot:[`item.discount_posting_group`]="{ item }">
              <v-chip
                color="blue-darken-1"
                size="x-small"
                variant="flat"
              >
                {{ item.discount_posting_group || 'N/A' }}
              </v-chip>
            </template>

            <!-- Source Code -->
            <template v-slot:[`item.source_code`]="{ item }">
              <p class="text-body-2 mt-2">
                {{ item.source_code || 'N/A' }}
              </p>
            </template>

            <!-- Document Type -->
            <template v-slot:[`item.document_type`]="{ item }">
              <v-chip
                :color="getDocumentTypeColor(item.document_type)"
                size="x-small"
                variant="flat"
                class="font-weight-bold"
              >
                {{ item.document_type || 'N/A' }}
              </v-chip>
            </template>

            <!-- Document No -->
            <template v-slot:[`item.document_no`]="{ item }">
              <p class="text-body-2 mt-2 font-weight-bold">
                {{ item.document_no || 'N/A' }}
              </p>
            </template>

            <!-- Amount -->
            <template v-slot:[`item.amount`]="{ item }">
              <p style="color: #01579B;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.amount) }}
              </p>
            </template>

            <!-- Remaining Amount -->
            <template v-slot:[`item.remaining_amount`]="{ item }">
              <p style="color: #E65100;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.remaining_amount) }}
              </p>
            </template>

            <!-- Assigned Amount -->
            <template v-slot:[`item.assigned_amount`]="{ item }">
              <p style="color: #2E7D32;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.assigned_amount) }}
              </p>
            </template>

            <!-- Actual Remaining Amount -->
            <template v-slot:[`item.actual_remaining_amount`]="{ item }">
              <p style="color: #C62828;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.actual_remaining_amount) }}
              </p>
            </template>

            <!-- System Remaining Amount -->
            <template v-slot:[`item.system_remaining_amount`]="{ item }">
              <p style="color: #6A1B9A;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.system_remaining_amount) }}
              </p>
            </template>

            <!-- System Assigned Amount -->
            <template v-slot:[`item.system_assigned_amount`]="{ item }">
              <p style="color: #00695C;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.system_assigned_amount) }}
              </p>
            </template>

            <!-- System Actual Remaining Amount -->
            <template v-slot:[`item.system_actual_remaining_amount`]="{ item }">
              <p style="color: #BF360C;" class="text-body-2 font-weight-medium mt-2">
                LKR {{ formatNumber(item.system_actual_remaining_amount) }}
              </p>
            </template>

            <!-- Salesperson Code -->
            <template v-slot:[`item.salesperson_code`]="{ item }">
              <p class="text-body-2 mt-2">
                {{ item.salesperson_code || 'N/A' }}
              </p>
            </template>

            <!-- AR Company -->
            <template v-slot:[`item.ar_company`]="{ item }">
              <p class="text-body-2 mt-2">
                {{ item.ar_company || 'N/A' }}
              </p>
            </template>

            <!-- Due Date -->
            <template v-slot:[`item.due_date`]="{ item }">
              <div class="column-content">
                {{ formatDate(item.due_date) }}
              </div>
            </template>

            <!-- Payment Status -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                :color="getPaymentStatusColor(item.status)"
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                {{ item.status || 'N/A' }}
              </v-chip>
            </template>

            <!-- Is Active (Document Status) -->
            <template v-slot:[`item.isActive`]="{ item }">
              <v-chip
                :color="isDocumentActive(item.isActive) ? 'success' : 'error'"
                size="small"
                variant="flat"
                class="font-weight-bold"
              >
                {{ isDocumentActive(item.isActive) ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

          </v-data-table>

          <!-- Custom Pagination Footer -->
          <v-divider class="mt-2"></v-divider>
          <div class="d-flex align-center justify-space-between pa-4">
            <div class="d-flex align-center">
              <span class="text-body-2 text-medium-emphasis mr-2">Items per page:</span>
              <v-select
                v-model="itemsPerPage"
                :items="itemsPerPageOptions"
                density="comfortable"
                variant="outlined"
                hide-details
                style="max-width: 100px"
                @update:modelValue="handleItemsPerPageChange"
              ></v-select>
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Showing {{ paginationInfo?.from || 0 }} to {{ paginationInfo?.to || 0 }} of {{ totalOutstandingsCount }} entries
            </div>
            <div class="d-flex align-center">
              <v-btn
                variant="text"
                icon="mdi-chevron-left"
                :disabled="page === 1 || tableLoading"
                @click="handlePageChange(page - 1)"
              ></v-btn>
              <span class="mx-2 text-body-2 font-weight-medium">
                Page {{ page }} of {{ totalPages || 1 }}
              </span>
              <v-btn
                variant="text"
                icon="mdi-chevron-right"
                :disabled="page === totalPages || totalPages === 0 || tableLoading"
                @click="handlePageChange(page + 1)"
              ></v-btn>
            </div>
          </div>

        </v-card-text>
      </v-card>

      <!-- Bulk Upload Dialog -->
      <v-dialog v-model="AreaBulkUploadDialog" fullscreen id="app_add_leads">
        <v-card flat>
          <v-toolbar style="border-radius: 0px !important;"
                     class="custom_fullscreen_dialog_toolbar pr-5 custom_card_box" color="white" dense>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-chip
              v-if="lastBulkUploadTime"
              color="#800080"
              variant="tonal"
              size="small"
              prepend-icon="mdi-clock-check-outline"
              class="mr-4"
            >
              Last Upload: {{ formattedLastUploadTime }}
            </v-chip>
            <v-btn
              variant="flat"
              style="background-color: #800080; color: white;"
              @click="closeBulkUploadDialog"
            >
              CLOSE
            </v-btn>
          </v-toolbar>

          <v-row class="mt-15 custom_scroll_option_fullscreen_dialog">
            <v-col cols="12" md="12">
              <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
                <template v-slot:prepend>
                  <v-icon>mdi-view-dashboard</v-icon>
                  <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">UPLOAD SPREADSHEET
                  </v-card-title>
                </template>
              </v-card>

              <v-card class="px-3 mx-6 mt-3 custom_card_box" elevation="1" border="sm opacity-1" rounded="lg">
                <v-row class="ma-0">
                  <v-col cols="12" xs="12" sm="12" md="5" lg="5">
                    <p class="text-body-1 font-weight-medium mb-4 mt-4">
                      Upload your outstanding bulk data set using excel or a csv file.
                    </p>

                    <v-alert
                      v-if="lastBulkUploadTime"
                      type="success"
                      variant="tonal"
                      density="compact"
                      class="mb-3"
                      icon="mdi-clock-check-outline"
                    >
                      <strong>Last Bulk Upload:</strong> {{ formattedLastUploadTime }}
                      <span v-if="lastBulkUploadBy" class="ml-2">
                        by <strong>{{ lastBulkUploadBy }}</strong>
                      </span>
                      <span v-if="lastBulkUploadCount" class="ml-2">
                        — <strong>{{ lastBulkUploadCount }}</strong> records uploaded
                      </span>
                    </v-alert>
                    <v-alert
                      v-else
                      type="info"
                      variant="tonal"
                      density="compact"
                      class="mb-3"
                      icon="mdi-information-outline"
                    >
                      No bulk upload has been performed yet.
                    </v-alert>

                    <v-divider></v-divider>

                    <v-card v-if="lastBulkUploadTime" class="mb-4" variant="outlined" color="#800080" rounded="lg">
                      <v-card-title class="text-subtitle-2 font-weight-bold" style="color:#800080;">
                        <v-icon size="small" class="mr-1">mdi-history</v-icon>
                        LAST UPLOAD DETAILS
                      </v-card-title>
                      <v-divider></v-divider>
                      <v-card-text class="py-2">
                        <v-row dense>
                          <v-col cols="12" class="py-1">
                            <div class="text-caption text-medium-emphasis">Upload Date & Time</div>
                            <div class="text-body-2 font-weight-medium">
                              <v-icon size="small" class="mr-1">mdi-calendar-clock</v-icon>
                              {{ formattedLastUploadTime }}
                            </div>
                          </v-col>
                          <v-col cols="12" class="py-1" v-if="lastBulkUploadBy">
                            <div class="text-caption text-medium-emphasis">Uploaded By</div>
                            <div class="text-body-2 font-weight-medium">
                              <v-icon size="small" class="mr-1">mdi-account</v-icon>
                              {{ lastBulkUploadBy }}
                            </div>
                          </v-col>
                          <v-col cols="12" class="py-1" v-if="lastBulkUploadCount">
                            <div class="text-caption text-medium-emphasis">Records Uploaded</div>
                            <div class="text-body-2 font-weight-medium">
                              <v-icon size="small" class="mr-1">mdi-database</v-icon>
                              {{ lastBulkUploadCount }} records
                            </div>
                          </v-col>
                          <v-col cols="12" class="py-1" v-if="lastBulkUploadFileName">
                            <div class="text-caption text-medium-emphasis">File Name</div>
                            <div class="text-body-2 font-weight-medium text-truncate">
                              <v-icon size="small" class="mr-1">mdi-file</v-icon>
                              {{ lastBulkUploadFileName }}
                            </div>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                      <v-file-input
                        ref="fileInput"
                        accept=".csv, .xls, .xlsx"
                        clearable
                        density="comfortable"
                        class="custom_input_field_color mt-3"
                        label="Upload your Excel or CSV file *"
                        counter
                        show-size
                        variant="outlined"
                        v-model="selectedFile"
                        @update:modelValue="handleFileUpload"
                        :loading="uploading"
                      ></v-file-input>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="12" lg="12" class="mt-2">
                      <v-btn
                        :disabled="!selectedFile || uploading"
                        :loading="uploading"
                        style="background-color: #800080; color: white;"
                        size="large"
                        block
                        @click="uploadBulkOutstandings"
                      >
                        <v-icon left>mdi-cloud-upload</v-icon>
                        UPLOAD OUTSTANDINGS
                      </v-btn>
                    </v-col>
                  </v-col>

                  <v-col class="d-flex align-center justify-center" cols="1">
                    <v-divider :thickness="2" vertical></v-divider>
                  </v-col>

                  <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                    <v-row>
                      <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                        <v-card class="d-flex align-center justify-space-between pa-4 mt-3" color="#F0F0F0" flat
                                width="100%">
                          <card class="mr-4" border="lg">
                            <p class="mb-0 text-body-2 font-weight-bold">DOWNLOAD SAMPLE TEMPLATE</p>
                          </card>
                          <v-divider vertical></v-divider>
                          <v-btn style="background-color: #800080; color: white;" size="x-large" variant="text" @click="downloadTemplate">
                            DOWNLOAD
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>

                    <v-row class="mt-4">
                      <v-col cols="12">
                        <v-alert
                          type="info"
                          variant="tonal"
                          class="mt-2"
                        >
                          <strong>File Format Requirements:</strong>
                          <ul class="mt-2">
                            <li>Accepted formats: .csv, .xls, .xlsx</li>
                            <li>Maximum file size: 10MB</li>
                            <li>Required fields: Customer No, Name, Amount, Document Type, Document No</li>
                            <li>Optional fields: Posting Date, Discount Posting Group, Source Code, etc.</li>
                          </ul>
                        </v-alert>
                      </v-col>
                    </v-row>

                    <v-row class="mt-2" v-if="lastBulkUploadTime">
                      <v-col cols="12">
                        <v-card variant="outlined" color="#800080" rounded="lg">
                          <v-card-title class="text-body-2 font-weight-bold" style="color:#800080;">
                            <v-icon size="small" class="mr-1">mdi-history</v-icon>
                            LAST UPLOAD SUMMARY
                          </v-card-title>
                          <v-divider></v-divider>
                          <v-card-text class="py-2">
                            <v-row dense>
                              <v-col cols="6">
                                <div class="text-caption text-medium-emphasis">Date & Time</div>
                                <div class="text-body-2 font-weight-medium">{{ formattedLastUploadTime }}</div>
                              </v-col>
                              <v-col cols="6" v-if="lastBulkUploadBy">
                                <div class="text-caption text-medium-emphasis">Uploaded By</div>
                                <div class="text-body-2 font-weight-medium">{{ lastBulkUploadBy }}</div>
                              </v-col>
                              <v-col cols="6" v-if="lastBulkUploadCount">
                                <div class="text-caption text-medium-emphasis">Records Uploaded</div>
                                <div class="text-body-2 font-weight-medium">{{ lastBulkUploadCount }}</div>
                              </v-col>
                              <v-col cols="6" v-if="lastBulkUploadFileName">
                                <div class="text-caption text-medium-emphasis">File Name</div>
                                <div class="text-body-2 font-weight-medium text-truncate">{{ lastBulkUploadFileName }}</div>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-card v-if="bulkItems.length > 0" flat class="mt-4">
                  <v-card-text>
                    <v-divider class="mb-4"></v-divider>
                    <div class="d-flex justify-space-between align-center mb-4">
                      <h3 class="text-h6">Preview Uploaded Data ({{ bulkItems.length }} items)</h3>
                      <v-btn
                        size="small"
                        color="error"
                        variant="text"
                        @click="clearPreview"
                      >
                        <v-icon left>mdi-close</v-icon> Clear Preview
                      </v-btn>
                    </div>
                    <v-data-table
                      :headers="bulkHeaders"
                      :items="bulkItems"
                      :items-per-page="5"
                      class="custom_table_curved"
                    >
                      <template v-slot:[`item.status`]="{ item }">
                        <v-chip
                          :color="item.status === 'Valid' ? 'success' : 'warning'"
                          size="small"
                          variant="flat"
                          class="font-weight-bold"
                        >
                          {{ item.status }}
                        </v-chip>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>

      <!-- Delete All Dialog -->
      <v-dialog v-model="deleteAllDialog" max-height="500" max-width="500" persistent>
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">DELETE ALL DATA</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="deleteAllDialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #BB0000" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  Do you want to delete all records?
                </v-card-title>

                <p style="opacity: 0.7;" class="text-body-2 mt-4 text-justify">
                  All outstanding records will be deleted immediately. Once deleted, they can no longer be used for any purpose.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_delete_btn" @click="executeDeleteAll" :loading="deleteAllLoading">DELETE ALL</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="deleteAllDialog = false">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackAlert.snackbar"
        :color="snackAlert.snackColor"
        :right="snackAlert.snackBarRight"
        :top="snackAlert.snackBarTop"
        timeout="3000"
      >
        {{ snackAlert.snackMessage }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackAlert.snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>

    </v-container>
  </v-app>
</template>

<script>
import axios from "axios";
import { getPermissions } from "@/plugins/permissions";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import _ from "lodash";

const LAST_UPLOAD_TIME_KEY = "outstanding_last_bulk_upload_time";
const LAST_UPLOAD_BY_KEY = "outstanding_last_bulk_upload_by";
const LAST_UPLOAD_COUNT_KEY = "outstanding_last_bulk_upload_count";
const LAST_UPLOAD_FILENAME_KEY = "outstanding_last_bulk_upload_filename";
const FILTERS_KEY = "outstanding_filters";

export default {
  name: "CollectionBulkUpload",

  data() {
    return {
      baseUrl: process.env.VUE_APP_API_BASE_URL,
      permissions: { read: false, write: false, delete: false },
      isAdministrator: false,

      isInitializing: false,

      panelOpen: ['filters'],
      search: "",
      page: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [5, 10, 25, 50, 100],
      outstandings: [],
      totalOutstandingsCount: 0,
      paginationInfo: null,
      tableLoading: false,
      searchTimeout: null,
      filterTimeout: null,

      filters: {
        date_from: null,
        date_to: null,
        document_type: null,
        ar_company: null,
        area_code: null,
        status: null,
        // FIXED: is_active is now ALWAYS one of: null, '1', '0' (strings)
        // or true/false from saved state — never an empty object or
        // the string "null".
        is_active: null
      },

      // FIXED: Single source of truth for the "Document Active Status"
      // dropdown. Values are kept as strings '1'/'0' so they map
      // predictably to Laravel's `boolean` validation rule. The
      // "All" option uses `null` and the select is made non-clearable
      // because "All" already represents the cleared state — this
      // prevents Vuetify from wiping the selection to `undefined`.
      activeStatusOptions: [
        { title: 'All', value: null },
        { title: 'Active', value: '1' },
        { title: 'Inactive', value: '0' }
      ],

      paymentStatusOptions: [
        { title: 'All', value: null },
        { title: 'UnPaid', value: 'UnPaid' },
        { title: 'Overdue', value: 'Overdue' },
        { title: 'Partially Paid', value: 'Partially Paid' }
      ],

      arCompanies: [
        'D.R.Home Appliances (Pvt) Ltd',
        'D.R.Industries Electronics',
        'Piyestra Furniture (Pvt) Ltd'
      ],

      documentTypes: [
        'Invoice',
        'Credit Memo',
        'JV or Not defined',
        'Payment'
      ],

      areaCodes: [],
      areaCodesLoading: false,
      allAreaData: [],

      lastBulkUploadTime: null,
      lastBulkUploadBy: null,
      lastBulkUploadCount: null,
      lastBulkUploadFileName: null,

      AreaBulkUploadDialog: false,
      selectedFile: null,
      uploading: false,
      bulkItems: [],
      deleteAllDialog: false,
      deleteAllLoading: false,

      bulkHeaders: [
        { text: "Posting Date", value: "Posting_Date" },
        { text: "Customer No", value: "Customer_No" },
        { text: "Region Code", value: "Region_Code" },
        { text: "Area Code", value: "Area_Code" },
        { text: "Name", value: "Name" },
        { text: "Document No", value: "Document_no" },
        { text: "Amount", value: "Amount" },
        { text: "Status", value: "status" },
      ],

      snackAlert: {
        snackbar: false,
        snackColor: "",
        snackMessage: "",
        snackBarRight: false,
        snackBarTop: true,
      },

      headersOutstandubg: [
        { title: "POSTING DATE", value: "posting_date", sortable: true },
        { title: "CUSTOMER NO", value: "customer_no", sortable: true },
        { title: "AREA CODE", value: "area_code", sortable: true },
        { title: "NAME", value: "name", sortable: true },
        { title: "DISCOUNT GROUP", value: "discount_posting_group", sortable: true },
        { title: "SOURCE CODE", value: "source_code", sortable: true },
        { title: "DOCUMENT TYPE", value: "document_type", sortable: true },
        { title: "DOCUMENT NO", value: "document_no", sortable: true },
        { title: "AMOUNT (LKR)", value: "amount", sortable: true },
        { title: "REMAINING (LKR)", value: "remaining_amount", sortable: true },
        { title: "ASSIGNED (LKR)", value: "assigned_amount", sortable: true },
        { title: "ACTUAL REMAINING (LKR)", value: "actual_remaining_amount", sortable: true },
        { title: "SYS REMAINING (LKR)", value: "system_remaining_amount", sortable: true },
        { title: "SYS ASSIGNED (LKR)", value: "system_assigned_amount", sortable: true },
        { title: "SYS ACTUAL REM (LKR)", value: "system_actual_remaining_amount", sortable: true },
        { title: "SALESPERSON", value: "salesperson_code", sortable: true },
        { title: "AR COMPANY", value: "ar_company", sortable: true },
        { title: "DUE DATE", value: "due_date", sortable: true },
        { title: "PAYMENT STATUS", value: "status", sortable: true },
        { title: "DOCUMENT STATUS", value: "isActive", sortable: true },
      ],
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalOutstandingsCount / this.itemsPerPage);
    },

    formattedLastUploadTime() {
      if (!this.lastBulkUploadTime) return null;
      try {
        const date = new Date(this.lastBulkUploadTime);
        return date.toLocaleString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch {
        return this.lastBulkUploadTime;
      }
    },

    hasActiveFilters() {
      const ia = this.filters.is_active;
      const hasIsActive = ia !== null && ia !== undefined && ia !== '';
      return !!(this.filters.date_from || this.filters.date_to ||
                this.filters.document_type || this.filters.ar_company ||
                this.filters.area_code || this.filters.status || hasIsActive);
    }
  },

  watch: {
    'filters.date_from': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },
    'filters.date_to': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },
    'filters.document_type': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },
    'filters.ar_company': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },
    'filters.area_code': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },
    'filters.status': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },
    'filters.is_active': {
      handler() {
        if (this.isInitializing) return;
        this.debouncedFilterChange();
      }
    },

    search: {
      handler() {
        if (this.isInitializing) return;
        this.debouncedSearchChange();
      }
    },
  },

  created() {
    this.debouncedFilterChange = _.debounce(() => {
      this.saveFiltersToLocalStorage();
      this.page = 1;
      this.fetchOutstandings();
    }, 500);

    this.debouncedSearchChange = _.debounce(() => {
      this.page = 1;
      this.fetchOutstandings();
    }, 500);
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem("token");
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    },

    checkAdministratorStatus() {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);

        if (value && value.includes('ADMINISTRATOR')) {
          if (key === 'user') {
            try {
              const parsed = JSON.parse(value);
              if (parsed.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
              if (parsed.user && parsed.user.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
            } catch (e) {
              console.error('Error parsing user key:', e);
            }
          }

          if (key === 'loginResponse') {
            try {
              const parsed = JSON.parse(value);
              if (parsed.user && parsed.user.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
              if (parsed.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
            } catch (e) {
              console.error('Error parsing loginResponse key:', e);
            }
          }

          if (key === 'userType') {
            if (value === 'ADMINISTRATOR') {
              this.isAdministrator = true;
              return;
            }
          }
        }
      }
    },

    getSanitizedHTML(html) {
      return DOMPurify.sanitize(html);
    },

    showSnackbar(message, color) {
      this.snackAlert.snackbar = true;
      this.snackAlert.snackMessage = message;
      this.snackAlert.snackColor = color;
    },

    saveFiltersToLocalStorage() {
      try {
        localStorage.setItem(FILTERS_KEY, JSON.stringify(this.filters));
      } catch (e) {
        console.warn("Could not save filters to localStorage:", e);
      }
    },

    // FIXED: When restoring saved filters, coerce is_active into the
    // exact shape the dropdown and buildFilterParams() expect:
    // null, '1' or '0' (string). This prevents stale values like
    // true/false, 1/0 (numbers) or the literal string "null" from
    // corrupting the filter.
    loadFiltersFromLocalStorage() {
      try {
        const savedFilters = localStorage.getItem(FILTERS_KEY);
        if (savedFilters) {
          const parsed = JSON.parse(savedFilters);

          let isActive = parsed.is_active;
          if (isActive === true || isActive === 1 || isActive === '1') {
            isActive = '1';
          } else if (isActive === false || isActive === 0 || isActive === '0') {
            isActive = '0';
          } else {
            isActive = null;
          }

          this.filters = {
            ...this.filters,
            ...parsed,
            is_active: isActive
          };
        }
      } catch (e) {
        console.warn("Could not load filters from localStorage:", e);
      }
    },

    clearAllFilters() {
      this.filters = {
        date_from: null,
        date_to: null,
        document_type: null,
        ar_company: null,
        area_code: null,
        status: null,
        is_active: null
      };
      this.saveFiltersToLocalStorage();
      this.page = 1;
      this.fetchOutstandings();
      this.showSnackbar("All filters cleared", "info");
    },

    clearDateFilter(filterKey) {
      this.filters[filterKey] = null;
      this.saveFiltersToLocalStorage();
      this.page = 1;
      this.fetchOutstandings();
      this.showSnackbar(`${filterKey.replace('_', ' ')} filter cleared`, "info");
    },

    // FIXED: The is_active value is ALWAYS normalised to a string
    // ('1' or '0') before being sent, and only sent when it is not
    // null/''/undefined. This guarantees Laravel's `boolean` rule
    // accepts it (it accepts "1"/"0" as strings) and that selecting
    // "Inactive" actually sends isActive=0.
    buildFilterParams() {
      const params = {};

      if (this.filters.date_from) params.date_from = this.filters.date_from;
      if (this.filters.date_to) params.date_to = this.filters.date_to;
      if (this.filters.document_type) params.document_type = this.filters.document_type;
      if (this.filters.ar_company) params.ar_company = this.filters.ar_company;
      if (this.filters.area_code) params.area_code = this.filters.area_code;
      if (this.filters.status) params.status = this.filters.status;

      const isActive = this.filters.is_active;
      if (isActive !== null && isActive !== undefined && isActive !== '') {
        const normalised = String(isActive);
        if (normalised === '1' || normalised === '0') {
          params.isActive = normalised;
        }
      }

      return params;
    },

    isDocumentActive(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },

    extractDropdownOptions(data) {
      if (!data || !Array.isArray(data)) return;

      this.allAreaData = data
        .filter(item => item.region_code && item.area_code)
        .map(item => ({
          region_code: item.region_code,
          area_code: item.area_code
        }));
    },

    async fetchUserAreas() {
      this.areaCodesLoading = true;
      try {
        const userId = localStorage.getItem("userId") || 1;

        const response = await axios.get(
          `${this.baseUrl}/api/area/user/${userId}`,
          {
            headers: this.getAuthHeaders(),
          }
        );

        console.log('✅ Areas API response:', response.data);

        let raw = [];

        if (Array.isArray(response.data)) {
          raw = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          raw = response.data.data;
        } else if (response.data && Array.isArray(response.data.areas)) {
          raw = response.data.areas;
        }

        const areaList = raw
          .map(item => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
              return item.area_code || item.areaCode || item.code || item.name || null;
            }
            return null;
          })
          .filter(v => v != null && v !== '');

        this.areaCodes = [...new Set(areaList)].sort();

        console.log(`📦 Loaded ${this.areaCodes.length} areas for user ${userId}`);
      } catch (error) {
        console.error("❌ Error fetching user areas:", error);

        this.showSnackbar(
          "Could not load areas from area service. Using fallback list.",
          "warning"
        );

        const fallback = this.outstandings
          .map(item => item.area_code)
          .filter(v => v);
        this.areaCodes = [...new Set(fallback)].sort();
      } finally {
        this.areaCodesLoading = false;
      }
    },

    async fetchFilterOptions() {
      try {
        const userId = localStorage.getItem("userId") || 1;
        const response = await axios.get(
          `${this.baseUrl}/api/outstanding-list-Pagination`,
          {
            params: { userId: userId, page: 1, perPage: 500 },
            headers: this.getAuthHeaders(),
          }
        );

        if (!response.data.success) return;

        let data = [];
        if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (response.data.data && response.data.data.data) {
          data = response.data.data.data;
        }

        this.extractDropdownOptions(data);
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    },

    loadLastUploadTime() {
      try {
        this.lastBulkUploadTime   = localStorage.getItem(LAST_UPLOAD_TIME_KEY)     || null;
        this.lastBulkUploadBy     = localStorage.getItem(LAST_UPLOAD_BY_KEY)       || null;
        this.lastBulkUploadCount  = localStorage.getItem(LAST_UPLOAD_COUNT_KEY)    || null;
        this.lastBulkUploadFileName = localStorage.getItem(LAST_UPLOAD_FILENAME_KEY) || null;

        if (this.lastBulkUploadTime) {
          this.fetchLatestBulkUploadInfo();
        }
      } catch (e) {
        console.warn("Could not load last upload time from localStorage:", e);
      }
    },

    async fetchLatestBulkUploadInfo() {
      try {
        const userId = localStorage.getItem("userId") || 1;
        const response = await axios.get(
          `${this.baseUrl}/api/outstandings/last-upload-info`,
          {
            params: { userId: userId },
            headers: this.getAuthHeaders(),
          }
        );

        if (response.data.success && response.data.data) {
          const uploadData = response.data.data;
          if (uploadData.last_upload_time) {
            this.saveLastUploadTime({
              time: uploadData.last_upload_time,
              by: uploadData.uploaded_by || this.lastBulkUploadBy,
              count: uploadData.records_count || this.lastBulkUploadCount,
              fileName: uploadData.file_name || this.lastBulkUploadFileName,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching latest bulk upload info:", error);
      }
    },

    saveLastUploadTime({ time, by, count, fileName }) {
      try {
        if (time)     localStorage.setItem(LAST_UPLOAD_TIME_KEY,     time);
        if (by)       localStorage.setItem(LAST_UPLOAD_BY_KEY,       by);
        if (count)    localStorage.setItem(LAST_UPLOAD_COUNT_KEY,    String(count));
        if (fileName) localStorage.setItem(LAST_UPLOAD_FILENAME_KEY, fileName);

        this.lastBulkUploadTime     = time     || this.lastBulkUploadTime;
        this.lastBulkUploadBy       = by       || this.lastBulkUploadBy;
        this.lastBulkUploadCount    = count    || this.lastBulkUploadCount;
        this.lastBulkUploadFileName = fileName || this.lastBulkUploadFileName;
      } catch (e) {
        console.warn("Could not save last upload time to localStorage:", e);
      }
    },

    async fetchOutstandings() {
      this.tableLoading = true;
      try {
        const userId = localStorage.getItem("userId") || 1;

        const params = {
          userId: userId,
          page: this.page,
          perPage: this.itemsPerPage,
          ...this.buildFilterParams()
        };

        if (this.search && this.search.trim()) {
          params.search = this.search.trim();
        }

        console.log('🔄 Fetching with params:', params);

        const response = await axios.get(
          `${this.baseUrl}/api/outstanding-list-Pagination`,
          {
            params: params,
            headers: this.getAuthHeaders(),
          }
        );

        console.log('✅ API Response:', response.data);

        if (response.data.success) {
          let data = [];
          let total = 0;
          let from = 1;
          let to = 10;

          if (response.data.data && Array.isArray(response.data.data)) {
            data = response.data.data;
            total = response.data.pagination?.total || data.length;
            from = response.data.pagination?.from || 1;
            to = response.data.pagination?.to || data.length;
          } else if (response.data.data && response.data.data.data) {
            data = response.data.data.data;
            total = response.data.data.total || data.length;
            from = response.data.data.from || 1;
            to = response.data.data.to || data.length;
          }

          this.outstandings = data;
          this.totalOutstandingsCount = total;
          this.paginationInfo = {
            from: from,
            to: to,
            total: total
          };

          console.log(`📊 Loaded ${data.length} items, total: ${total}`);

          if (response.data.last_bulk_upload_at) {
            this.saveLastUploadTime({
              time: response.data.last_bulk_upload_at,
              by: response.data.last_bulk_upload_by || null,
              count: response.data.last_bulk_upload_count || null,
              fileName: response.data.last_bulk_upload_filename || null,
            });
          }
        } else {
          this.outstandings = [];
          this.totalOutstandingsCount = 0;
          this.paginationInfo = null;
          if (response.data.message) {
            this.showSnackbar(response.data.message, "info");
          }
        }
      } catch (error) {
        console.error("❌ Error fetching outstandings:", error);
        this.handleApiError(error);
        this.outstandings = [];
        this.totalOutstandingsCount = 0;
        this.paginationInfo = null;
      } finally {
        this.tableLoading = false;
      }
    },

    handlePageChange(newPage) {
      console.log('📄 Page change requested:', newPage);
      if (newPage < 1 || newPage > this.totalPages) {
        console.log('⛔ Page out of range:', newPage);
        return;
      }
      if (newPage !== this.page) {
        this.page = newPage;
        this.fetchOutstandings();
      }
    },

    handleItemsPerPageChange() {
      console.log('📄 Items per page changed to:', this.itemsPerPage);
      this.page = 1;
      this.fetchOutstandings();
    },

    handleApiError(error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          this.showSnackbar("Unauthorized. Please log in again.", "error");
        } else if (status === 403) {
          this.showSnackbar("You do not have permission to perform this action.", "error");
        } else if (status === 422 && data.error) {
          const errorMessages = Object.values(data.error).flat().join(" ");
          this.showSnackbar(errorMessages, "error");
        } else if (data.message) {
          this.showSnackbar(data.message, "error");
        } else if (data.error) {
          this.showSnackbar(`Error: ${data.error}`, "error");
        } else {
          this.showSnackbar("An unexpected error occurred.", "error");
        }
      } else if (error.request) {
        this.showSnackbar("Network error. Please check your connection.", "error");
      } else {
        this.showSnackbar("An unexpected error occurred.", "error");
      }
    },

    confirmDeleteAll() {
      if (this.totalOutstandingsCount === 0) {
        this.showSnackbar("No outstandings found to delete.", "warning");
        return;
      }
      this.deleteAllDialog = true;
    },

    async executeDeleteAll() {
      this.deleteAllLoading = true;
      try {
        const userId = localStorage.getItem('userId') || 1;
        const token = localStorage.getItem('token');

        const response = await axios.delete(
          `${this.baseUrl}/api/outstanding/delete-all`,
          {
            data: {
              userId: parseInt(userId),
              confirm: true
            },
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          this.outstandings = [];
          this.totalOutstandingsCount = 0;
          this.paginationInfo = null;
          this.page = 1;

          const deletedCount = response.data.deleted_count || 0;
          this.showSnackbar(`✅ All ${deletedCount} outstanding records deleted successfully`, 'success');

          await this.fetchOutstandings();
          await this.fetchFilterOptions();
          await this.fetchUserAreas();
        } else {
          throw new Error(response.data?.message || 'Failed to delete all outstandings');
        }
      } catch (error) {
        console.error('Error deleting all outstandings:', error);
        this.handleApiError(error);
      } finally {
        this.deleteAllLoading = false;
        this.deleteAllDialog = false;
      }
    },

    backPageRoute() {
      this.$router.back();
    },

    handleFileUpload(file) {
      if (!file) {
        this.selectedFile = null;
        this.bulkItems = [];
        return;
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showSnackbar("File size exceeds 10MB limit.", "error");
        this.selectedFile = null;
        this.bulkItems = [];
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          this.bulkItems = jsonData.map((row, index) => {
            const postingDate = row["Posting Date"] || row["Posting_Date"] || "";
            const customerNo  = row["Customer No"] || row["Customer_No"] || row["customer_no"] || row["CUSTOMER_NO"] || "";
            const regionCode  = row["Region Code"] || row["Region_Code"] || "";
            const areaCode    = row["Area Code"] || row["Area_Code"] || "";
            const name        = row["Name"] || row["name"] || row["NAME"] || "";
            const documentType = row["Document Type"] || row["Document_Type"] || row["document_type"] || row["DOCUMENT_TYPE"] || "";
            const documentNo  = row["Document No"] || row["Document_no"] || row["document_no"] || row["DOCUMENT_NO"] || "";
            const amount      = row["Amount"] || row["amount"] || row["AMOUNT"] || 0;

            const isValid = postingDate && customerNo && name && documentType && documentNo && amount;

            return {
              id: index + 1,
              Posting_Date: postingDate,
              Customer_No: customerNo,
              Region_Code: regionCode,
              Area_Code: areaCode,
              Name: name,
              Document_Type: documentType,
              Document_no: documentNo,
              Amount: amount,
              Discount_Posting_Group: row["Discount Posting Group"] || row["Discount_Posting_Group"] || "",
              Source_code: row["Source Code"] || row["Source_Code"] || row["Source code"] || row["Source_code"] || "",
              Remaining_Amount: row["Remaining Amount"] || row["Remaining_Amount"] || amount,
              Assigned_Amount: row["Assigned Amount"] || row["Assigned_Amount"] || 0,
              Actual_Remaining_Amount: row["Actual Remaining Amount"] || row["Actual_Remaining_Amount"] || amount,
              Salesperson_Code: row["Salesperson Code"] || row["Salesperson_Code"] || "",
              AR_Company: row["AR Company"] || row["AR_Company"] || "",
              Due_Date: row["Due Date"] || row["Due_Date"] || "",
              status: isValid ? "Valid" : "Missing Required Fields",
            };
          });

          const validCount   = this.bulkItems.filter(item => item.status === "Valid").length;
          const invalidCount = this.bulkItems.length - validCount;

          this.showSnackbar(
            `Loaded ${this.bulkItems.length} records from file. Valid: ${validCount}, Invalid: ${invalidCount}`,
            invalidCount > 0 ? "warning" : "success"
          );
        } catch (error) {
          console.error("Error parsing file:", error);
          this.showSnackbar("Error parsing file. Please check the file format.", "error");
          this.selectedFile = null;
          this.bulkItems = [];
        }
      };
      reader.readAsArrayBuffer(file);
    },

    downloadTemplate() {
      const templateData = [
        [
          "Posting Date", "Customer No","Area Code", "Name",
          "Discount Posting Group", "Source Code", "Document Type", "Document No",
          "Amount", "Remaining Amount", "Assigned Amount", "Actual Remaining Amount",
          "Salesperson Code", "AR Company", "Due Date"
        ],
        [
          "2024-01-15", "C001","A01", "ABC Company",
          "DOMESTIC", "MANUAL", "Invoice", "INV-001",
          "1000.00", "1000.00", "0.00", "1000.00", "SP001", "Piyestra", "2024-02-15"
        ],
        [
          "2024-01-16", "C002", "R02","XYZ Corporation",
          "INTERNATIONAL", "IMPORT", "Credit Memo", "CM-001",
          "500.00", "500.00", "0.00", "500.00", "SP002", "Piyestra", "2024-02-16"
        ],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(templateData);
      worksheet["!cols"] = [
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 },
        { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Outstanding Template");
      XLSX.writeFile(workbook, "Outstanding_Bulk_Upload_Template.xlsx");
      this.showSnackbar("Template downloaded successfully.", "success");
    },

    async uploadBulkOutstandings() {
      if (!this.selectedFile) {
        this.showSnackbar("Please select a file to upload.", "error");
        return;
      }

      const validItems = this.bulkItems.filter(item => item.status === "Valid");
      if (validItems.length === 0) {
        this.showSnackbar("No valid data to upload. Please check your file.", "error");
        return;
      }

      this.uploading = true;

      try {
        const userId   = localStorage.getItem("userId");
        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("file", this.selectedFile);

        const response = await axios.post(
          `${this.baseUrl}/api/outstandings/bulk-upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
          }
        );

        if (response.data.success) {
          const uploadTimestamp = response.data.uploaded_at || new Date().toISOString();
          const uploadedBy      = response.data.uploaded_by || localStorage.getItem("userName") || `User #${userId}`;
          const uploadedCount   = response.data.records_count || validItems.length;
          const uploadedFile    = this.selectedFile ? this.selectedFile.name : null;

          this.saveLastUploadTime({
            time:     uploadTimestamp,
            by:       uploadedBy,
            count:    uploadedCount,
            fileName: uploadedFile,
          });

          this.showSnackbar(
            response.data.message || `Bulk upload completed successfully. ${uploadedCount} records uploaded.`,
            "success"
          );

          await this.fetchOutstandings();
          await this.fetchFilterOptions();
          await this.fetchUserAreas();
          this.closeBulkUploadDialog();
        } else {
          this.showSnackbar(
            response.data.message || "Bulk upload failed.",
            "error"
          );
        }
      } catch (error) {
        console.error("Error in bulk upload:", error);
        if (error.response) {
          const { data } = error.response;
          this.showSnackbar(
            data.message || data.error || "Bulk upload failed. Please try again.",
            "error"
          );
        } else if (error.request) {
          this.showSnackbar("Network error. Please check your connection.", "error");
        } else {
          this.showSnackbar("An unexpected error occurred.", "error");
        }
      } finally {
        this.uploading = false;
      }
    },

    closeBulkUploadDialog() {
      this.AreaBulkUploadDialog = false;
      this.selectedFile = null;
      this.bulkItems = [];
      this.uploading = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },

    clearPreview() {
      this.bulkItems = [];
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },

    homeReload() {
      this.$router.push("/");
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    },

    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },

    getDocumentTypeColor(type) {
      const colors = {
        'Invoice': 'green-darken-2',
        'Payment': 'blue-darken-2',
        'Credit Memo': 'orange-darken-2',
        'JV or Not defined': 'purple-darken-2'
      };
      return colors[type] || 'grey-darken-2';
    },

    getPaymentStatusColor(status) {
      const colors = {
        'UnPaid': 'error',
        'Overdue': 'warning',
        'Partially Paid': 'info'
      };
      return colors[status] || 'grey-darken-2';
    }
  },

  mounted() {
    this.permissions = getPermissions(0, "Module Name");
    this.checkAdministratorStatus();

    if (!this.permissions.read) {
      this.showSnackbar ? this.showSnackbar("Access Denied", "error") : alert("Access Denied");
      this.$router.push('/dashboard');
      return;
    }

    this.isInitializing = true;

    this.loadLastUploadTime();
    this.loadFiltersFromLocalStorage();
    this.fetchFilterOptions();
    this.fetchUserAreas();
    this.fetchOutstandings();

    this.$nextTick(() => {
      this.isInitializing = false;
    });

    if (this.$route.query.openBulkUpload === 'true') {
      this.AreaBulkUploadDialog = true;
    }
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  }
};
</script>

<style scoped>
.custom_company_logo_nav {
  width: 110px;
  height: 35px;
}

.custom_delete_btn {
  background-color: #C62828 !important;
  color: white !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 0 20px;
  height: 40px;
  box-shadow: 0 2px 4px rgba(198, 40, 40, 0.2);
}

.custom_delete_btn:hover:not(:disabled) {
  background-color: #B71C1C !important;
  box-shadow: 0 4px 8px rgba(198, 40, 40, 0.3);
}

.custom_delete_btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.custom_btn_secondary {
  background-color: #f0f0f0 !important;
  color: #333 !important;
  font-weight: 500;
  border-radius: 8px;
}

.custom_btn_secondary:hover {
  background-color: #e0e0e0 !important;
}

.column-content {
  font-size: 13px;
  color: #616161;
}

@import "../../styles/collection_management/collection_management.scss";
</style>